import InformationalDialog from 'components/dialogs/InformationalDialog';
import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from 'services/AuthService';
import CompanyService from 'services/CompanyService';
import CountriesService from 'services/CountriesService';
import { CompanyModel, UpdateCompanySettingsModel, CompanySize, CountryModel, RegionModel } from 'services/EpdClient';
import styled from 'styled-components';
import { ErrorText, InfoText, Label, SaveButton, SuccessText } from 'styles/Styles.styled';
import Spinner from 'styles/spinner-left.styled';
import { Option } from 'types/types';
import { PoorMansError } from 'util/utils';
import * as Yup from 'yup';

import {
  AuLicenseesCountry,
  GLOBAL_OPTION,
  MAX_COMPANY_LOGO_SIZE,
  ProcessStatus,
  REG_PHONE,
  RoleName,
} from '../../constants/constants';
import useCurrentUser from 'hooks/useCurrentUser';
import getAvailableRegionsForCountryOptions from './getAvailableRegionsForCountryOptions';
import AvailableCountryRegions from './AvailableCountryRegions';
import InputFormField from 'components/v2/form/InputFormField';
import SelectDataField from 'components/v2/form/select-data-field/SelectDataField';
import { HelpBox } from 'components/v2/help-boxes';
import { FormikImageUpload } from 'components/v2/images-upload/FormikImageUpload';
import {
  ALLOWED_SYMBOLS_ERROR,
  allowedGeneralCharsRegex,
  allowedSymbolsLink,
  emailSmartValidation,
  renderAllowedSymbolsError,
  websiteSmartValidation,
} from 'util/allowedSymbolsValidationUtils';
import { Theme } from 'styles/theme';
import { useTheme } from 'styled-components';

const LOGO_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/bmp'];

type FormikValuesBase = Omit<CompanyModel, 'marketplace'> & { marketplace?: Option[] };

type FormikValues = Omit<FormikValuesBase, 'website' | 'organizationEmail' | 'logotype'> & {
  website?: string | null;
  organizationEmail?: string | null;
  logotype?: File | string | null;
};

const ManageSettings: React.FunctionComponent<{ companyId: string }> = ({ companyId }) => {
  const theme = useTheme() as Theme;

  const [company, setCompany] = useState<CompanyModel>({} as CompanyModel);
  const [regions, setRegions] = useState<RegionModel[]>([]);
  const [marketplaceCountries, setMarketplaceCountries] = useState<Option[]>([]);
  const [companyLogotype, setCompanyLogotype] = useState<any>();
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [imgStatus, setImgStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [countries, setCountries] = useState<CountryModel[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { t } = useTranslation();
  const allCountriesRef = useRef<CountryModel[]>([]);
  const formikRef = useRef<FormikProps<FormikValues>>(null);
  const [showAUCountryChangedDialog, setShowAUCountryChangedDialog] = useState<boolean>(false);

  const currentUserEmail = useCurrentUser().email;
  const epdOwners = company?.memberships?.filter((m) => m.roleName === RoleName.EPD_OWNER)?.map((m) => m.userEmail);
  const isEpdOwner = epdOwners?.includes(currentUserEmail!);
  const isLcaPractitioner = company?.isLcaPractitioner;
  const companySizeOptions = Object.values(CompanySize).filter(
    (x) => !(company?.isEpdOwner && x === CompanySize.NotApplicable)
  );

  const REQUIRED_MSG = t('messages.required');
  const ZIP_LENGTH_ERROR = 'Please enter a ZIP code between 3 and 20 characters long.';

  const companySchema = Yup.object<FormikValues>({
    name: Yup.string().nullable().required(t('messages.required')).matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    organizationEmail: emailSmartValidation(t).nullable().required(t('messages.required')),
    website: websiteSmartValidation(t)
      .nullable()
      .when('isEpdOwner', {
        is: true,
        then: (schema: any) => schema.required(t('messages.required')),
      }),
    phone: (Yup.string() as any).matches(REG_PHONE, t('messages.invalidPhone')).nullable(),
    companySize: (Yup.string() as any).nullable().when('isEpdOwner', {
      is: true,
      then: (schema: any) =>
        schema
          .required(t('messages.required'))
          .notOneOf([CompanySize.NotApplicable], 'Please select a valid organisation size option'),
    }),
    abn: (Yup.string() as any)
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when('organizationCountryId', {
        is: (countryId: string | undefined) =>
          countryId && AuLicenseesCountry.includes(countries.find((x) => x.id === countryId)?.alpha2Code || ''),
        then: Yup.string().required(t('messages.required')),
      })
      .nullable(),
    gst: (Yup.string() as any)
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when('organizationCountryId', {
        is: (countryId: string | undefined) =>
          countryId && AuLicenseesCountry.includes(countries.find((x) => x.id === countryId)?.alpha2Code || ''),
        then: Yup.string().required(t('messages.required')),
      })
      .nullable(),
    organizationAddressLine: Yup.string()
      .nullable()
      .required(t('messages.required'))
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    organizationAddressLineSecond: Yup.string()
      .nullable()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR) as Yup.StringSchema,
    organizationAddressCity: Yup.string()
      .nullable()
      .required(t('messages.required'))
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    organizationAddressZipCode: Yup.string()
      .nullable()
      .trim()
      .required(t('messages.required'))
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .transform((value) => value?.trim())
      .min(3, ZIP_LENGTH_ERROR)
      .max(20, ZIP_LENGTH_ERROR) as Yup.StringSchema,
    organizationCountryId: Yup.string().nullable().required(t('messages.required')),
    regionId: Yup.mixed<string | null>().when('organizationCountryId', {
      is: (organizationCountryId: string | undefined) =>
        getAvailableRegionsForCountryOptions(organizationCountryId || '', regions).length > 0,
      then: Yup.string().trim().required(t('messages.required')),
    }),
    marketplace: (Yup.array() as any).when('isMarketplaceEnabled', {
      is: true,
      then: (schema: any) => schema.min(1, t('messages.required')),
    }),
  });

  const isSpecialAuRules = (selectedCountryId: string | undefined) => {
    return AuLicenseesCountry.includes(allCountriesRef.current?.find((x) => x.id === selectedCountryId)?.alpha2Code || '');
  };

  const fetchCompany = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      const result = await CompanyService.getCompany(companyId);
      fetchCountries(result?.address?.countryId);
      setCompany(result);
      const regionsResult = await CountriesService.getCountryRegions();
      setRegions(regionsResult);
    } catch (error) {
      setErrorMessage(PoorMansError(error));
    }
    setStatus(ProcessStatus.None);
  };

  const fetchCompanyLogotype = async () => {
    try {
      setImgStatus(ProcessStatus.Fetching);
      const result = await CompanyService.getLogotype(companyId);
      if (result !== null) {
        const binary = new Uint8Array(result).reduce((acc, byte) => acc + String.fromCharCode(byte), '');
        setCompanyLogotype('data:image/png;base64,' + btoa(binary));
      } else {
        setCompanyLogotype(null);
      }
      setImgStatus(ProcessStatus.Success);
    } catch (error) {
      setErrorMessage(PoorMansError(error));
      setImgStatus(ProcessStatus.Error);
    }
  };

  useEffect(() => {
    async function fetch() {
      if (!allCountriesRef.current || allCountriesRef.current.length == 0) {
        const countries = await CountriesService.getCountries();
        allCountriesRef.current = countries;
      }
    }
    fetch();
  }, []);

  useEffect(() => {
    fetchCompany();
    fetchCompanyLogotype();
  }, [companyId]);

  const handleCountryChange = (
    selectedOption: Option | null,
    formCountryId: string | undefined,
    values: FormikValues,
    setValues: any
  ) => {
    const countryId = selectedOption?.value;
    const AUCountryId = allCountriesRef.current?.find((c) => c.alpha2Code === 'AU')?.id;
    const changedFromAustralia = formCountryId === AUCountryId;
    const changedToAustralia = AUCountryId === countryId;

    if ((isEpdOwner || AuthService.isLicenseeAdmin()) && (changedFromAustralia || changedToAustralia)) {
      setShowAUCountryChangedDialog(true);
      return;
    }
    // Two separate setFieldValue calls race each other's validation (each uses a stale values
    // snapshot), leaving isValid wrong until the next blur. Update both fields in one pass instead.
    setValues({ ...values, organizationCountryId: countryId, regionId: '' });
  };

  const fetchCountries = async (countryId: string | undefined) => {
    const showAllCountry = AuthService.isAdmin();
    const result = showAllCountry ? allCountriesRef.current : await CountriesService.getLicenseeCountries(companyId);
    setCountries(result);
    setMarketplaceCountries([
      GLOBAL_OPTION,
      ...result.map((country) => {
        return { value: country.id, label: country.name } as Option;
      }),
    ]);
  };

  const updateCompany = async (rowValues: FormikValues) => {
    const settingsModel: UpdateCompanySettingsModel = {
      id: rowValues.id,
      name: rowValues.name,
      address: rowValues.address,
      organizationEmail: rowValues.organizationEmail ?? undefined,
      phone: rowValues.phone,
      website: rowValues.website ?? undefined,
      companySize: rowValues.companySize,
      gst: rowValues.gst,
      abn: rowValues.abn,
      organizationAddressLine: rowValues.organizationAddressLine,
      organizationAddressLineSecond: rowValues.organizationAddressLineSecond,
      organizationAddressCity: rowValues.organizationAddressCity,
      organizationAddressZipCode: rowValues.organizationAddressZipCode,
      organizationCountryId: rowValues.organizationCountryId,
      regionId: rowValues.regionId,
      isGlobalMarketplace: rowValues.marketplace?.some((c) => c.label === 'Global'),
      marketplace: rowValues.marketplace?.filter((c) => c.label !== 'Global').map((c) => ({ id: c.value, name: c.label })),
      needDataUpdating: rowValues.needDataUpdating,
    };

    try {
      await CompanyService.updateCompanySettings(companyId, settingsModel);
      await saveLogotypeIfChanged(rowValues.logotype);
      await fetchCompany();
      await fetchCompanyLogotype();
      setStatus(ProcessStatus.Success);
    } catch (error) {
      setStatus(ProcessStatus.Error);
      setErrorMessage(PoorMansError(error));
    }
  };

  const saveLogotypeIfChanged = async (logotype: File | string | null | undefined) => {
    if (logotype instanceof File) {
      await CompanyService.updateLogotype(companyId, { fileName: logotype.name, data: logotype });
    } else if (logotype == null && companyLogotype != null) {
      await CompanyService.updateLogotype(companyId);
    }
  };

  if (status === ProcessStatus.Fetching) {
    return <InfoText>{t('manageSettings.messages.fetching')}</InfoText>;
  }

  if (!company?.id && (errorMessage || status === ProcessStatus.Error)) {
    return <ErrorText>{t('manageSettings.errors.technical')}</ErrorText>;
  }

  const handleChangeMarketplace = (options: Option[]) => {
    const globalOption = options?.find((option) => option.label === 'Global');
    if (globalOption) {
      return [globalOption];
    }
    return options;
  };

  const initialMarketplaceCountries = company?.isGlobalMarketplace
    ? [GLOBAL_OPTION]
    : company?.marketplace?.map((country) => {
        return { label: country.name ?? '', value: country.id ?? '' };
      });

  const initialValues: FormikValues = {
    ...company,
    marketplace: initialMarketplaceCountries,
    logotype: companyLogotype ?? null,
  };

  // Marks every field touched from the start so validation errors show right after the form loads, not only after blur.
  const initialTouched = Object.keys(initialValues).reduce((touched, key) => {
    touched[key] = true;
    return touched;
  }, {} as Record<string, boolean>);

  return (
    <>
      <h3>{t('manageSettings.title')}</h3>
      <ContentWrapper>
        <CompanySettingsHelpBox>
          <span>{t('manageSettings.info.info1')}</span>
        </CompanySettingsHelpBox>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          initialTouched={initialTouched as any}
          enableReinitialize
          validationSchema={companySchema}
          onSubmit={updateCompany}
          validateOnMount
          validateOnBlur
        >
          {({ values, setFieldValue, setValues, isSubmitting, isValid, dirty, errors }) => {
            const isCurrentSizeUnavailable =
              !!values.companySize && !companySizeOptions.some((x) => x === values.companySize);
            const companySizeSelectOptions: Option[] = [
              ...companySizeOptions.map((x) => ({ value: x, label: x } as Option)),
              ...(isCurrentSizeUnavailable ? [{ value: values.companySize!, label: values.companySize! } as Option] : []),
            ];
            const showAuFields = isSpecialAuRules(values.organizationCountryId);
            const commonInputProps = {
              errorPositioning: 'absolute' as const,
              shouldShowBottomError: (err: string) => err !== REQUIRED_MSG,
              shouldHighlightBorder: (err: string) => err !== REQUIRED_MSG,
              renderErrorMessage: (err: string) =>
                err === ALLOWED_SYMBOLS_ERROR ? renderAllowedSymbolsError({ theme, allowedSymbolsLink }) : err,
            };

            return (
              <>
                <LogoSection>
                  <Label style={{ margin: '4px', fontFamily: 'Inter', fontSize: '12px' }}>{'Logo'}</Label>
                  {imgStatus === ProcessStatus.Fetching ? (
                    <Spinner inline />
                  ) : (
                    <FormikImageUpload
                      name="logotype"
                      maxFileSizeMB={MAX_COMPANY_LOGO_SIZE / (1024 * 1024)}
                      allowedTypes={LOGO_ALLOWED_TYPES}
                    />
                  )}
                </LogoSection>
                <FormGrid noValidate>
                  <InputFormField
                    label={t('addressLine1')}
                    placeholder={t('placeholder.typeHere')}
                    name="organizationAddressLine"
                    required
                    {...commonInputProps}
                  />

                  <InputFormField
                    label={t('organization.name')}
                    name="name"
                    placeholder={t('placeholder.typeHere')}
                    disabled={!company.hasChangeNamePermissions}
                    required
                    trim
                    {...commonInputProps}
                  />

                  <InputFormField
                    label={t('addressLine2')}
                    placeholder={t('placeholder.typeHere')}
                    name="organizationAddressLineSecond"
                    {...commonInputProps}
                  />

                  <InputFormField
                    label={t('website')}
                    name="website"
                    placeholder={t('placeholder.website')}
                    required={!!company?.isEpdOwner}
                    tooltip={
                      isLcaPractitioner
                        ? "Your website will appear on the 'LCA Consultants' page at environdec.com"
                        : undefined
                    }
                    tooltipPosition="title"
                    trim
                    {...commonInputProps}
                  />

                  <InputFormField
                    label={t('organizationAddressCity')}
                    placeholder={t('placeholder.typeHere')}
                    name="organizationAddressCity"
                    required
                    {...commonInputProps}
                  />

                  <InputFormField
                    label={t('phone')}
                    placeholder={t('placeholder.typeHere')}
                    name="phone"
                    {...commonInputProps}
                  />

                  <InputFormField
                    label={t('organizationAddressZipCode')}
                    placeholder={t('placeholder.typeHere')}
                    name="organizationAddressZipCode"
                    required
                    trim
                    {...commonInputProps}
                  />

                  <InputFormField
                    label={t('organizationEmail')}
                    name="organizationEmail"
                    placeholder={t('placeholder.email')}
                    required
                    trim
                    {...commonInputProps}
                  />

                  <SelectDataField
                    label={t('country')}
                    placeholder={t('placeholder.select')}
                    name="organizationCountryId"
                    required
                    isMulti={false}
                    options={countries.map((c) => ({ value: c.id, label: c.name } as Option))}
                    onChange={(value: Option | null) =>
                      handleCountryChange(value, values.organizationCountryId, values, setValues)
                    }
                    {...commonInputProps}
                  />

                  <SelectDataField
                    label={t('organizationSize')}
                    placeholder={t('placeholder.select')}
                    name="companySize"
                    required={!!company?.isEpdOwner}
                    isMulti={false}
                    isOptionDisabled={(option: unknown) =>
                      isCurrentSizeUnavailable && (option as Option).value === values.companySize
                    }
                    options={companySizeSelectOptions}
                    {...commonInputProps}
                  />

                  <AvailableCountryRegions
                    name="regionId"
                    countryId={values.organizationCountryId ?? null}
                    regions={regions}
                    label="Region"
                  />

                  {company?.isMarketplaceEnabled && (
                    <SelectDataField
                      label="Marketplace"
                      placeholder={t('placeholder.select')}
                      name="marketplace"
                      required
                      isMulti
                      options={marketplaceCountries}
                      value={values.marketplace}
                      onChange={(opts: readonly Option[]) =>
                        setFieldValue('marketplace', handleChangeMarketplace([...opts]))
                      }
                      {...commonInputProps}
                    />
                  )}

                  {showAuFields && (
                    <>
                      <InputFormField label={t('abn')} name="abn" required {...commonInputProps} />
                      <InputFormField label={t('gst')} name="gst" required {...commonInputProps} />
                    </>
                  )}

                  <SaveButton
                    disabled={isSubmitting || !isValid || !dirty}
                    style={{ fontSize: '18px', height: '48px', padding: '0' }}
                  >
                    {isSubmitting ? t('messages.saving') : t('messages.saveSettings')}
                  </SaveButton>

                  <div style={{ gridColumn: '1 / -1', padding: '1rem' }}>
                    {status === ProcessStatus.Error && <ErrorText>{t('manageSettings.errors.notSaved')}</ErrorText>}
                    {status === ProcessStatus.Success && <SuccessText>{t('manageSettings.messages.saved')}</SuccessText>}
                  </div>
                </FormGrid>
              </>
            );
          }}
        </Formik>
      </ContentWrapper>
      <InformationalDialog
        isOpened={showAUCountryChangedDialog}
        header="Organisation country change is not allowed"
        onHide={() => setShowAUCountryChangedDialog(false)}
      >
        Country change is not allowed as your organisation is specified as a regional licensee. Please contact the support of
        EPD international at support@environdec.com to get the assistance.
      </InformationalDialog>
    </>
  );
};

const FormGrid = styled(Form)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: start;

  @media (max-width: 1001px) {
    grid-template-columns: 1fr;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const CompanySettingsHelpBox = styled(HelpBox)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 13px 16px;
`;

export default ManageSettings;
